import React, { useState, useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Doctors from './components/Doctors';
import Upload from './components/Upload';
import Batches from './components/Batches';
import Templates from './components/Templates';
import Home from './components/Home';
import Login from './components/Login';
import Performance from './components/Performance';
import Maintenance from './components/Maintenance';
import SessionWarningModal from './components/SessionWarningModal';
import './App.css';

const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const PERFORMANCE_ADMIN = 'mabdulhussein@heartteam.com';

const HomeIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const UploadIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const BatchIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const DoctorIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const SignOutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const TemplateIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="9" y1="21" x2="9" y2="9"/>
  </svg>
);

const HamburgerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const PerformanceIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const ListIcon = () => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const pageMeta = {
  home:        { title: 'Home',                subtitle: 'Choose a workflow to get started' },
  upload:      { title: 'Full Extraction',     subtitle: 'Extract all patient details from a scanned batch' },
  name_only:   { title: 'Name Only',           subtitle: 'Extract patient names and sorted list only' },
  batches:     { title: 'Batch History',       subtitle: 'View and manage previous batches' },
  doctors:     { title: 'Manage Doctors',      subtitle: 'Configure the provider list' },
  templates:   { title: 'Facesheet Templates', subtitle: 'Auto-collected layout library' },
  performance: { title: 'Model Performance',   subtitle: 'Track and compare AI model extraction accuracy over time' },
};

function AppShell() {
  const [activePage, setActivePage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const { userName, userEmail, instance, sessionWarning, setSessionWarning, resetIdleTimer } = useAuth();
  const isAdmin = userEmail?.toLowerCase() === PERFORMANCE_ADMIN;

  const meta = pageMeta[activePage];

  const navigate = (page) => {
    setActivePage(page);
    setMenuOpen(false);
  };

  // Close drawer on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const initials = userName
    ? userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : userEmail[0]?.toUpperCase() || '?';

  return (
    <div className="app-layout">
      <SessionWarningModal
        warning={sessionWarning}
        onStayLoggedIn={() => { resetIdleTimer(); setSessionWarning(null); }}
      />

      {/* Mobile overlay */}
      {menuOpen && <div className="sidebar-overlay" onClick={() => setMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar${menuOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1, minWidth: 0 }}>
            <img
              src="/HVILogo.png"
              alt="Heart & Vascular Institute"
              style={{ width: 40, height: 40, flexShrink: 0 }}
            />
            <div>
              <div className="sidebar-brand-name">Heart &amp; Vascular</div>
              <div className="sidebar-brand-sub">Clinic Batch Sorter</div>
            </div>
          </div>
          <button className="sidebar-close-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Workflow</div>

          <button
            className={`nav-item${activePage === 'home' ? ' active' : ''}`}
            onClick={() => navigate('home')}
          >
            <HomeIcon />
            Home
          </button>

          <button
            className={`nav-item nav-green${activePage === 'name_only' ? ' active' : ''}`}
            onClick={() => navigate('name_only')}
          >
            <ListIcon />
            Name Only
          </button>

          <button
            className={`nav-item nav-blue${activePage === 'upload' ? ' active' : ''}`}
            onClick={() => navigate('upload')}
          >
            <UploadIcon />
            Full Extraction
          </button>

          <button
            className={`nav-item${activePage === 'batches' ? ' active' : ''}`}
            onClick={() => navigate('batches')}
          >
            <BatchIcon />
            Batch History
          </button>

          <div className="sidebar-section-label" style={{ marginTop: '0.75rem' }}>Settings</div>

          <button
            className={`nav-item${activePage === 'doctors' ? ' active' : ''}`}
            onClick={() => navigate('doctors')}
          >
            <DoctorIcon />
            Doctors
          </button>

          <button
            className={`nav-item${activePage === 'templates' ? ' active' : ''}`}
            onClick={() => navigate('templates')}
          >
            <TemplateIcon />
            Templates
          </button>

          {isAdmin && (
            <>
              <div className="sidebar-section-label" style={{ marginTop: '0.75rem' }}>Admin</div>
              <button
                className={`nav-item${activePage === 'performance' ? ' active' : ''}`}
                onClick={() => navigate('performance')}
              >
                <PerformanceIcon />
                Model Performance
              </button>
            </>
          )}
        </nav>

        {/* Signed-in user + sign out */}
        <div style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid var(--border)',
          marginTop: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
            <div style={{
              width: 28, height: 28,
              background: 'var(--primary-light)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)',
              flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userName || userEmail}
              </div>
              {userName && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userEmail}
                </div>
              )}
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            style={{ width: '100%', justifyContent: 'center', gap: '0.4rem', color: 'var(--text-3)' }}
            onClick={handleSignOut}
          >
            <SignOutIcon /> Sign out
          </button>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-footer-text">ClinicSorter v4.3</div>
        </div>
      </aside>

      {/* Main area */}
      <div className="main-area">
        <header className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button className="hamburger-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <HamburgerIcon />
            </button>
            <div>
              <div className="page-title">{meta.title}</div>
              <div className="page-subtitle">{meta.subtitle}</div>
            </div>
          </div>
        </header>

        <div className="page-body">
          {activePage === 'home'        && <Home onNavigate={setActivePage} />}
          {activePage === 'upload'      && <Upload mode="full" />}
          {activePage === 'name_only'   && <Upload mode="name_only" />}
          {activePage === 'batches'     && <Batches />}
          {activePage === 'doctors'     && <Doctors />}
          {activePage === 'templates'   && <Templates />}
          {activePage === 'performance' && isAdmin && <Performance />}
        </div>

        <footer className="main-footer">
          <span>Developed by <strong>Moose</strong></span>
          <span>Heart Team Records &nbsp;·&nbsp; ClinicSorter v4.3</span>
        </footer>
      </div>
    </div>
  );
}

function App() {
  const isAuthenticated = useIsAuthenticated();

  if (MAINTENANCE_MODE) return <Maintenance />;

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
